import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Star, AlertTriangle } from "lucide-react";
import api from "../lib/api";

/**
 * Blocks-in-place banner shown when the current user has delivered orders
 * that still require a rating. New orders / new bids are refused server-side
 * (HTTP 409) until every pending review is submitted.
 */
export default function PendingRatingsBanner({ role, testId = "pending-ratings-banner" }) {
  const [pending, setPending] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      try {
        const { data } = await api.get("/orders/pending-rating");
        if (mounted) setPending(Array.isArray(data) ? data : []);
      } catch (_) {
        if (mounted) setPending([]);
      } finally {
        if (mounted) setLoaded(true);
      }
    };
    load();
    const t = setInterval(load, 20000);
    return () => { mounted = false; clearInterval(t); };
  }, []);

  if (!loaded || pending.length === 0) return null;

  const first = pending[0];
  const cta =
    role === "supplier"
      ? "Recensire per tornare a offrire"
      : "Recensire per creare nuovi ordini";

  return (
    <div
      className="mt-4 flex items-start gap-3 rounded-2xl border border-amber-300 bg-amber-50 p-4"
      data-testid={testId}
    >
      <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
      <div className="flex-1 min-w-0">
        <div className="text-sm font-semibold text-amber-900">
          Valutazione consegna obbligatoria · {pending.length} ordine
          {pending.length === 1 ? "" : "i"} in sospeso
        </div>
        <div className="text-xs text-amber-800 mt-0.5">
          Per procedere con nuovi{" "}
          {role === "supplier" ? "offerte / accettazioni express" : "ordini"} devi lasciare una recensione ad ogni consegna ricevuta.
        </div>
        <div className="mt-2 flex flex-wrap gap-2">
          {pending.slice(0, 3).map((p) => (
            <Link
              key={p.order_id}
              to={`/ordine/${p.order_id}`}
              className="inline-flex items-center gap-1 rounded-full bg-white border border-amber-300 px-3 py-1 text-xs font-mono text-amber-900 hover:bg-amber-100"
              data-testid={`pending-rating-link-${p.order_id}`}
            >
              <Star className="w-3 h-3" /> {p.title}
            </Link>
          ))}
          {pending.length > 3 && (
            <span className="text-xs text-amber-800 font-mono">
              +{pending.length - 3} altri
            </span>
          )}
        </div>
      </div>
      <Link
        to={`/ordine/${first.order_id}`}
        className="shrink-0 inline-flex items-center gap-1.5 rounded-full bg-amber-600 hover:bg-amber-700 text-white px-4 py-2 text-xs font-semibold"
        data-testid="pending-ratings-primary-cta"
      >
        <Star className="w-3.5 h-3.5" /> {cta}
      </Link>
    </div>
  );
}
